customElements.define('ec-cpm-fabric', class extends HTMLElement {
  constructor() {
    super();

    this.startContainerEl = null;
    this.groupContainerElements = null;
    this.colourInputElements = null;
    this.fabricTypeSelectEl = null;
    this.emptyMessageEl = null;
    this.resetEl = null;

    this.activeColorFilters = new Set();
    this.selectedFabricType = '';
    this.groupVisibilityMap = new Map(); // Stores { visibleByType: bool, visibleByColor: bool }

    // Binding necessary for event listeners and potential detachment later
    this.handleColourChange = this.handleColourChange.bind(this);
    this.handleFabricTypeChange = this.handleFabricTypeChange.bind(this);
    this.handleReset = this.handleReset.bind(this);
    // Note: applyFilters, updateGroupVisibility, init are called internally,
    // binding isn't strictly required unless passed as callbacks elsewhere.
  }

  connectedCallback() {
    this.startContainerEl = this.querySelector('.ec-cpm__grid.ec-cpm__grid--6.hide');
    this.groupContainerElements = this.querySelectorAll('.ec-cpm__fabric-group');
    this.colourInputElements = this.querySelectorAll('.ec-cpm__filter .ec-cpm__filter-colour input');
    this.filterContainerEl = this.querySelector('.ec-cpm__filter');
    this.fabricTypeSelectEl = this.querySelector('.ec-cpm__filter .ec-cpm__filter-fabric select');
    this.emptyMessageEl = this.querySelector('.ec-cpm__filter-empty');
    this.resetEl = this.querySelector('[id^="Reset-"]');

    if (this.groupContainerElements.length < 1) {
      console.warn('No group container elements found. Using basic layout');
      this.startContainerEl?.classList.remove('hide');
      this.filterContainerEl?.classList.add('hide');
      this.emptyMessageEl?.classList.add('hide');
      return;
    }

    this.groupVisibilityMap.clear(); // Clear in case of re-connection
    this.groupContainerElements.forEach(group => {
      const fabricType = group.getAttribute('data-fabric-type');
      if (fabricType) { // Ensure type exists
        this.groupVisibilityMap.set(fabricType, {
          visibleByType: true, visibleByColor: true
        });
      }
    });

    this.colourInputElements.forEach(input =>
      input.addEventListener('change', this.handleColourChange));
    // Add null checks for robustness
    this.fabricTypeSelectEl?.addEventListener('change', this.handleFabricTypeChange);
    this.resetEl?.addEventListener('click', this.handleReset);

    this.init();
    this.expandAccordion()
  }

  disconnectedCallback() {
    this.colourInputElements.forEach(input =>
      input.removeEventListener('change', this.handleColourChange));
    this.fabricTypeSelectEl?.removeEventListener('change', this.handleFabricTypeChange);
    this.resetEl?.removeEventListener('click', this.handleReset);
  }

  handleColourChange(e) {
    const input = e.target;
    const colorValue = input.value;
    if (input.checked) {
      this.activeColorFilters.add(colorValue);
    } else {
      this.activeColorFilters.delete(colorValue);
    }
    this.applyFilters();
  }

  expandAccordion() {
   document.querySelectorAll('.ec-cpm-accordion').forEach(e => {
      if (e.querySelector('summary div').innerHTML.indexOf(".") != -1) {
        e.setAttribute("open", "")
      }
    });
  }

  handleFabricTypeChange(e) {
    this.selectedFabricType = e.target.value;

    // Update visibility by type for all groups
    this.groupVisibilityMap.forEach((groupState, fabricType) => {
      groupState.visibleByType = !this.selectedFabricType || fabricType === this.selectedFabricType;
    });

    this.applyFilters();
  }

  handleReset(e) {
    e?.preventDefault(); // Check if event exists

    // Reset Fabric Type Select
    if (this.fabricTypeSelectEl) {
      this.fabricTypeSelectEl.value = '';
    }
    this.selectedFabricType = '';

    // Reset Color Inputs
    this.colourInputElements.forEach(input => input.checked = false);
    this.activeColorFilters.clear();

    // Reset Visibility Map State
    this.groupVisibilityMap.forEach(state => {
      state.visibleByType = true;
      state.visibleByColor = true;
    });

    // Show all individual items (remove hide class)
    this.querySelectorAll('[data-fabric-colour]')
      .forEach(item => item.classList.remove('hide'));

    // Update group visibility based on reset state (all should be visible)
    this.updateGroupVisibility(); // This will remove 'hide' from groups

    // Hide Empty/Reset messages
    this.emptyMessageEl?.classList.add('hide');
    this.resetEl?.classList.add('hide');
  }

  applyFilters() {
    let totalVisibleItems = 0;
    const noColorsSelected = this.activeColorFilters.size === 0;

    this.groupContainerElements.forEach(group => {
      const fabricType = group.getAttribute('data-fabric-type');
      const groupState = this.groupVisibilityMap.get(fabricType);

      if (!groupState) return; // Skip if group type not found in map

      let groupHasVisibleItems = false;
      const items = group.querySelectorAll('[data-fabric-colour]');

      items.forEach(item => {
        // Determine item visibility based on color filter
        const itemColor = item.getAttribute('data-fabric-colour');
        // Item is visible if no colors are selected OR its color is in the active set
        const isVisibleByColor = noColorsSelected || this.activeColorFilters.has(itemColor);

        item.classList.toggle('hide', !isVisibleByColor); // Add 'hide' if NOT visible

        if (isVisibleByColor) {
          groupHasVisibleItems = true;
        }
      });

      // Update group's visibility based on color filtering result
      groupState.visibleByColor = groupHasVisibleItems || noColorsSelected; // A group is visible by color if it has items OR no color filter is active

      // Check if this group contributes visible items *overall*
      if (groupState.visibleByType && groupState.visibleByColor) {
        // Count only items that are *actually* visible (not hidden by color filter)
        totalVisibleItems += Array.from(items).filter(item => !item.classList.contains('hide')).length;
      }

    });

    // Update visibility of group containers themselves
    this.updateGroupVisibility();

    // Show/hide the empty message and reset button based on total count
    const showEmpty = totalVisibleItems === 0;
    this.emptyMessageEl?.classList.toggle('hide', !showEmpty); // Remove 'hide' if showEmpty is true
    this.resetEl?.classList.toggle('hide', !showEmpty);         // Remove 'hide' if showEmpty is true
  }

  updateGroupVisibility() {
    this.groupContainerElements.forEach(group => {
      const fabricType = group.getAttribute('data-fabric-type');
      const state = this.groupVisibilityMap.get(fabricType);
      // Group is hidden if it's filtered out by type OR by color
      const shouldHide = !(state?.visibleByType && state?.visibleByColor);
      group.classList.toggle('hide', shouldHide);
    });
  }

  init() {
    // Move items from start container (if it exists)
    if (this.startContainerEl) {
      Array.from(this.startContainerEl.children).forEach(child => {
        const type = child.getAttribute('data-fabric-type');
        // Find the correct target grid *within* the group
        const targetGrid = this.querySelector(
          `.ec-cpm__fabric-group[data-fabric-type="${type}"] .ec-cpm__grid` // Assuming grid is child of group
        );
        if (targetGrid) {
          targetGrid.appendChild(child);
        } else {
          console.warn(`Target grid for fabric type "${type}" not found during init.`);
          child.remove();
          // Decide how to handle orphaned children: remove, append elsewhere, or leave them?
          // child.remove(); // Option: remove if target not found
        }
      });
      this.startContainerEl.remove();
      this.startContainerEl = null; // Clear reference
    }

    // Ensure all groups start visible (remove hide class) - redundant if CSS default is visible
    // this.groupContainerElements.forEach(group => group.classList.remove('hide'));

    // Initial state for messages: hidden
    this.emptyMessageEl?.classList.add('hide');
    this.resetEl?.classList.add('hide');

    // Apply initial filters (if any pre-selected states existed, though currently reset)
    // Or just ensure the initial view is correct based on default state
    this.applyFilters(); // Run filters once to set the initial correct view
  }
});